import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Chip,
  Snackbar,
  Divider,
  IconButton,
} from '@material-ui/core';
import {
  FileCopy as CopyIcon,
  Save as SaveIcon,
  Edit as EditIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { useMutation, useQuery } from '@apollo/client';
import AffiliateLayout from '../../components/affiliate/AffiliateLayoutSimple';
import { CURRENT_AFFILIATE_QUERY } from '../../core/graphql/queries/affiliate';
import { UPDATE_AFFILIATE_PROFILE_MUTATION } from '../../core/graphql/mutations/affiliate';

const useStyles = makeStyles((theme) => ({
  profileCard: {
    backgroundColor: '#1a2332',
    border: '1px solid #333',
    borderRadius: 8,
    marginBottom: theme.spacing(3),
  },
  profileContent: {
    padding: theme.spacing(3),
  },
  sectionTitle: {
    color: '#fff',
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
  },
  formField: {
    marginBottom: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#2d4a3e',
      '& fieldset': {
        borderColor: '#333',
      },
      '&:hover fieldset': {
        borderColor: '#4caf50',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#4caf50',
      },
    },
    '& .MuiInputLabel-root': {
      color: '#ccc',
    },
    '& .MuiOutlinedInput-input': {
      color: '#fff',
    },
  },
  referralCard: {
    backgroundColor: '#1a2332',
    border: '1px solid #333',
    borderRadius: 8,
    marginBottom: theme.spacing(3),
  },
  referralContent: {
    padding: theme.spacing(3),
  },
  referralInput: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#2d4a3e',
      '& fieldset': {
        borderColor: '#333',
      },
      '&:hover fieldset': {
        borderColor: '#4caf50',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#4caf50',
      },
    },
    '& .MuiInputLabel-root': {
      color: '#ccc',
    },
    '& .MuiOutlinedInput-input': {
      color: '#fff',
    },
  },
  statusCard: {
    backgroundColor: '#1a2332',
    border: '1px solid #333',
    borderRadius: 8,
    marginBottom: theme.spacing(3),
  },
  statusContent: {
    padding: theme.spacing(3),
  },
  statusChip: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
  },
  approved: {
    backgroundColor: '#4caf50',
    color: '#fff',
  },
  pending: {
    backgroundColor: '#ff9800',
    color: '#fff',
  },
  suspended: {
    backgroundColor: '#f44336',
    color: '#fff',
  },
  actionButton: {
    backgroundColor: '#2d4a3e',
    border: '1px solid #4caf50',
    color: '#4caf50',
    '&:hover': {
      backgroundColor: '#4caf50',
      color: '#fff',
    },
  },
  commissionInfo: {
    backgroundColor: '#2d4a3e',
    padding: theme.spacing(2),
    borderRadius: 8,
    marginTop: theme.spacing(2),
  },
  commissionTitle: {
    color: '#4caf50',
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  commissionText: {
    color: '#ccc',
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },
}));

const AffiliateProfile: React.FC = () => {
  const classes = useStyles();
  const [isEditing, setIsEditing] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    company: '',
    paypalEmail: '',
    bankAccountNumber: '',
    bankRoutingNumber: '',
  });

  const { data, loading, error } = useQuery(CURRENT_AFFILIATE_QUERY, {
    errorPolicy: 'all',
  });

  const [updateProfile] = useMutation(UPDATE_AFFILIATE_PROFILE_MUTATION, {
    onCompleted: () => {
      setSnackbarMessage('Profile updated successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setIsEditing(false);
    },
    onError: (error) => {
      setSnackbarMessage(`Error updating profile: ${error.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    },
  });

  const affiliate = data?.myAffiliate;

  // Update form data when affiliate loads - moved before early returns to fix React Hook rules
  React.useEffect(() => {
    if (affiliate) {
      setFormData({
        name: affiliate.name || '',
        email: affiliate.email || '',
        phoneNumber: affiliate.phoneNumber || '',
        company: affiliate.company || '',
        paypalEmail: affiliate.paymentInfo?.paypalEmail || '',
        bankAccountNumber: affiliate.paymentInfo?.bankAccountNumber || '',
        bankRoutingNumber: affiliate.paymentInfo?.bankRoutingNumber || '',
      });
    }
  }, [affiliate]);

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSave = async () => {
    try {
      await updateProfile({
        variables: {
          input: {
            name: formData.name,
            email: formData.email,
            phoneNumber: formData.phoneNumber,
            company: formData.company,
            paymentInfo: {
              paypalEmail: formData.paypalEmail,
              bankAccountNumber: formData.bankAccountNumber,
              bankRoutingNumber: formData.bankRoutingNumber,
            },
          },
        },
      });
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  const handleCopyReferralCode = async () => {
    try {
      await navigator.clipboard.writeText(affiliate?.uniqueCode || '');
      setSnackbarMessage('Referral code copied to clipboard!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage('Failed to copy referral code');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const getStatusChip = (status: string) => {
    const statusConfig = {
      approved: { label: 'Approved', className: classes.approved },
      active: { label: 'Approved', className: classes.approved },
      pending: { label: 'Pending', className: classes.pending },
      suspended: { label: 'Suspended', className: classes.suspended },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    
    return (
      <Chip
        label={config.label}
        className={`${classes.statusChip} ${config.className}`}
        size="small"
      />
    );
  };

  if (loading) {
    return (
      <AffiliateLayout title="Profile" subtitle="Manage your affiliate profile and settings.">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <Typography style={{ color: '#fff' }}>Loading profile...</Typography>
        </Box>
      </AffiliateLayout>
    );
  }

  if (error) {
    return (
      <AffiliateLayout title="Profile" subtitle="Manage your affiliate profile and settings.">
        <Box textAlign="center" padding={4}>
          <Typography variant="h6" style={{ color: '#f44336' }} gutterBottom>
            Error Loading Profile
          </Typography>
          <Typography variant="body2" style={{ color: '#ccc' }}>
            Unable to load profile information. Please try again later.
          </Typography>
        </Box>
      </AffiliateLayout>
    );
  }

  return (
    <AffiliateLayout
      title="Profile"
      subtitle="Manage your affiliate profile and payment settings."
    >
      {/* Account Status */}
      <Card className={classes.statusCard}>
        <CardContent className={classes.statusContent}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Account Status
          </Typography>
          <Box display="flex" alignItems="center" style={{ gap: '16px' }} marginBottom={2}>
            <Typography variant="body1" style={{ color: '#fff' }}>
              Status:
            </Typography>
            {getStatusChip(affiliate?.status || 'pending')}
          </Box>
          <Typography variant="body2" style={{ color: '#ccc' }}>
            {(affiliate?.status === 'active' || affiliate?.status === 'approved') &&
              'Your account is approved and you can start earning commissions!'}
            {affiliate?.status === 'pending' && 'Your account is under review. You will be notified once approved.'}
            {affiliate?.status === 'suspended' && 'Your account has been suspended. Please contact support for assistance.'}
          </Typography>
        </CardContent>
      </Card>

      {/* Profile Information */}
      <Card className={classes.profileCard}>
        <CardContent className={classes.profileContent}>
          <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Profile Information
            </Typography>
            <Button
              variant="outlined"
              startIcon={isEditing ? <SaveIcon /> : <EditIcon />}
              onClick={isEditing ? handleSave : () => setIsEditing(true)}
              className={classes.actionButton}
            >
              {isEditing ? 'Save Changes' : 'Edit Profile'}
            </Button>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.name}
                onChange={handleInputChange('name')}
                className={classes.formField}
                disabled={!isEditing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                className={classes.formField}
                disabled={!isEditing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={handleInputChange('phoneNumber')}
                className={classes.formField}
                disabled={!isEditing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company (Optional)"
                value={formData.company}
                onChange={handleInputChange('company')}
                className={classes.formField}
                disabled={!isEditing}
              />
            </Grid>
          </Grid>

          <Divider style={{ margin: '24px 0', borderColor: '#333' }} />

          <Typography variant="h6" className={classes.sectionTitle}>
            Payment Information
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="PayPal Email"
                type="email"
                value={formData.paypalEmail}
                onChange={handleInputChange('paypalEmail')}
                className={classes.formField}
                disabled={!isEditing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bank Account Number"
                value={formData.bankAccountNumber}
                onChange={handleInputChange('bankAccountNumber')}
                className={classes.formField}
                disabled={!isEditing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bank Routing Number"
                value={formData.bankRoutingNumber}
                onChange={handleInputChange('bankRoutingNumber')}
                className={classes.formField}
                disabled={!isEditing}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Referral Code */}
      <Card className={classes.referralCard}>
        <CardContent className={classes.referralContent}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Your Referral Code
          </Typography>
          <TextField
            fullWidth
            label="Referral Code"
            value={affiliate?.uniqueCode || ''}
            className={classes.referralInput}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton onClick={handleCopyReferralCode} style={{ color: '#4caf50' }}>
                  <CopyIcon />
                </IconButton>
              ),
            }}
          />
          <Typography variant="body2" style={{ color: '#ccc', marginTop: 8 }}>
            Share this code with artists to earn commissions when they join StarFarm.
          </Typography>
        </CardContent>
      </Card>

      {/* Commission Information */}
      <Card className={classes.profileCard}>
        <CardContent className={classes.profileContent}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Commission Structure
          </Typography>
          <Box className={classes.commissionInfo}>
            <Typography variant="h6" className={classes.commissionTitle}>
              How Commissions Work
            </Typography>
            <Typography variant="body2" className={classes.commissionText}>
              • Earn 10% commission on all revenue generated by artists you refer<br/>
              • Commissions are calculated monthly and paid out within 30 days<br/>
              • Minimum payout threshold: $50<br/>
              • Commissions are paid via PayPal or direct bank transfer<br/>
              • You can track your earnings in the Dashboard and Conversions sections
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
      >
        <div style={{ 
          backgroundColor: snackbarSeverity === 'error' ? '#f44336' : '#4caf50', 
          color: 'white', 
          padding: '12px 16px', 
          borderRadius: '4px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{snackbarMessage}</span>
          <button 
            onClick={() => setSnackbarOpen(false)}
            style={{ 
              background: 'none', 
              border: 'none', 
              color: 'white', 
              cursor: 'pointer',
              fontSize: '18px'
            }}
          >
            ×
          </button>
        </div>
      </Snackbar>
    </AffiliateLayout>
  );
};

export default AffiliateProfile;
